import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { Button, CircularProgress, Link, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import {
  GridColDef,
  GridDensity,
  gridFilteredSortedRowIdsSelector,
  GridFilterModel,
  GridPaginationModel,
  GridRowId,
  useGridApiRef,
} from '@mui/x-data-grid';
import { StudyUploadVisibilityOperation } from 'api/uploads';
import {
  DEFAULT_READ_ONLY_GRID_FILTER_MODEL,
  getReadOnlyDateTimeFilterOperators,
  hasActiveReadOnlyGridFilters,
  ReadOnlyDataGrid,
} from 'components/atoms/BaseDataGrid/ReadOnlyDataGrid';
import CopyableText from 'components/atoms/CopyableText';
import { SlidePreviewCell } from 'components/atoms/DataGridRenderers/SlideIdCellRenderer';
import { UploadFileCell } from 'components/atoms/DataGridRenderers/UploadFileCell';
import { autoGridRowHeight } from 'components/atoms/EditableDataGrid/helpers';
import { HighlightedText } from 'components/atoms/HighlightedText';
import { SlideIntakeLifecyclePresentation, SlideIntakeRequestState } from 'components/SlideIntake/lifecycle';
import TileGenerationStatusLine from 'components/TileGeneration/TileGenerationStatusLine';
import { FileInfo, MrxsUploadPackageSummary, UploadCaseSummary, UploadDashboardRow } from 'interfaces/fileList';
import { IngestJobSummary } from 'interfaces/ingestJob';
import { Slide } from 'interfaces/slide';
import { compact, filter, includes, join, map, some } from 'lodash';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { formatDateTimeForUser, toDate } from 'utils/dateTime';
import { formatBytes } from 'utils/helpers';
import { isTileGenerationActive, useSlidesTileGenerationStatus } from 'utils/queryHooks/useSlidesTileGenerationStatus';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { getUrlToJobDetails } from 'utils/useNavigationToJobsPage';
import { useNavigationToViewerPage } from 'utils/useNavigationToViewerPage';

import { getUploadDashboardLifecycle, isUploadIngestActionAvailable } from './helpers';
import IngestJobStatusCell from './IngestJobStatusCell';
import UploadIngestTimeline, { getUploadTimelineIngests, getUploadTimelineSearchText } from './UploadIngestTimeline';

const S3_UPLOAD_PATH_SUFFIX_PATTERN = /^s3:\/\/[^/]+\/ATOM\/(.+)$/;
const DISABLE_VIRTUALIZATION_FOR_TESTS = process.env.NODE_ENV === 'test';

const getDisplayDate = (value: string) =>
  formatDateTimeForUser(value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
const getDisplaySize = (value: number) => formatBytes(value);

const getUploadSizeQuickFilterFn: GridColDef<UploadDashboardRow>['getApplyQuickFilterFn'] = (value) => {
  const normalizedQuery = String(value ?? '')
    .trim()
    .toLowerCase();

  if (!normalizedQuery) {
    return null;
  }

  return (_columnValue, row) => {
    const displaySize = getDisplaySize(row.size).toLowerCase();

    return displaySize.includes(normalizedQuery) || String(row.size).includes(normalizedQuery);
  };
};

const getUploadDateValue = (value?: string | null) => toDate(value);

const getUploadDateQuickFilterFn: GridColDef<UploadDashboardRow>['getApplyQuickFilterFn'] = (value) => {
  const normalizedQuery = String(value ?? '')
    .trim()
    .toLowerCase();

  if (!normalizedQuery) {
    return null;
  }

  return (_columnValue, row) => {
    const displayDate = getDisplayDate(row.date).toLowerCase();

    return (
      displayDate.includes(normalizedQuery) ||
      String(row.date || '')
        .toLowerCase()
        .includes(normalizedQuery)
    );
  };
};

interface UploadsTableProps {
  rows: UploadDashboardRow[];
  selectedStudyId?: string;
  onHideUploads?: (rows: UploadDashboardRow[]) => void;
  onRestoreUploads?: (rows: UploadDashboardRow[]) => void;
  onDownloadManifest?: (row: UploadDashboardRow) => Promise<void> | void;
  onExecuteUploadIngest?: (row: UploadDashboardRow) => void;
  onRunManualPostIngestPipeline?: (row: UploadDashboardRow) => void;
  onExecuteBulkUploadIngest?: (rows?: UploadDashboardRow[]) => void;
  onAssignUploadToOwnCase?: (row: UploadDashboardRow) => void;
  onAssignSelectedUploadsToSingleCase?: (rows: UploadDashboardRow[]) => void;
  onAssignSelectedUploadsToOwnCases?: (rows: UploadDashboardRow[]) => void;
  onSetUploadVisibility?: (row: UploadDashboardRow, operation: StudyUploadVisibilityOperation) => void;
  onRemoveCaseAssignments?: (row: UploadDashboardRow) => void;
  pendingAssignmentRemovalIds?: readonly string[];
  canExecuteUploadIngest?: boolean;
  canReingestUploadedSlides?: boolean;
  canRunManualPostIngestPipeline?: boolean;
  canManageUploadVisibility?: boolean;
  downloadingManifestKey?: string | null;
  uploadVisibilityPendingOperations?: Readonly<Record<string, StudyUploadVisibilityOperation>>;
  lifecycleRequestState?: SlideIntakeRequestState;
}

export const getBulkHideableOtherFiles = (
  rows: readonly UploadDashboardRow[],
  visibleRowIds?: ReadonlySet<string> | null
) =>
  rows.filter((row) => {
    if (visibleRowIds && !visibleRowIds.has(String(row.id))) {
      return false;
    }

    return !row.isHidden && Boolean(row.canHide) && !row.isManifest && !row.uploadKey;
  });

const getDisplayedPath = (path: string) => {
  const pathMatch = path.match(S3_UPLOAD_PATH_SUFFIX_PATTERN);

  return pathMatch?.[1] || path;
};

const getFullUploadPath = ({ path, fileName }: Pick<FileInfo, 'path' | 'fileName'>) => {
  if (!fileName || path.endsWith(`/${fileName}`)) {
    return path;
  }

  return `${path}/${fileName}`;
};

const isExtensionlessUploadRow = (row: Pick<UploadDashboardRow, 'isManifest' | 'type' | 'uploadKey'>) =>
  Boolean(row.uploadKey && !row.isManifest && !row.type);

const getQuickFilterQuery = (filterModel: GridFilterModel) =>
  (filterModel.quickFilterValues || [])
    .map((value) => String(value ?? ''))
    .join(' ')
    .trim();

const getUploadCaseValue = (cases: UploadCaseSummary[] | null) =>
  join(
    map(cases || [], ({ id, displayName }) => `${displayName} ${id}`.trim()),
    ' '
  ).trim();

const getUploadStatusJobReference = (
  row: UploadDashboardRow,
  lifecycle: SlideIntakeLifecyclePresentation
): { job: IngestJobSummary; archived?: true } | null => {
  if (lifecycle.state === 'ingestFailed' && row.latestFailedIngestJob) {
    return {
      job: row.latestFailedIngestJob,
    };
  }

  if (lifecycle.state === 'ingesting' && row.activeIngestJob) {
    return {
      job: row.activeIngestJob,
    };
  }

  if (lifecycle.state === 'rejectedIngest' && row.latestSuccessfulIngestJob) {
    return {
      job: row.latestSuccessfulIngestJob,
      archived: true,
    };
  }

  if (row.latestSuccessfulIngestJob) {
    return {
      job: row.latestSuccessfulIngestJob,
    };
  }

  if (row.latestIngestJob) {
    return {
      job: row.latestIngestJob,
    };
  }

  return null;
};

const getUploadIngestActionLabel = (row: UploadDashboardRow, lifecycle: SlideIntakeLifecyclePresentation) => {
  if (lifecycle.actionCategory === 'retryIngest') {
    return 'Retry ingest';
  }

  return lifecycle.actionCategory === 'trackIngest' ? 'Ingest' : 'Re-ingest';
};

const isAssignableMissingCaseRow = (row: UploadDashboardRow, lifecycle: SlideIntakeLifecyclePresentation) =>
  lifecycle.actionCategory === 'organizeSlides' && Boolean(row.slideId);

const MAX_LISTED_MISSING_MRXS_FILES = 3;

const getMrxsPackageCaption = (mrxsPackage: MrxsUploadPackageSummary) => {
  const fileCountLabel =
    mrxsPackage.expectedFileCount === null
      ? `${mrxsPackage.fileCount} files`
      : `${mrxsPackage.fileCount} of ${mrxsPackage.expectedFileCount} files`;

  if (mrxsPackage.isComplete) {
    return `MRXS package · ${fileCountLabel} · Complete`;
  }

  if (mrxsPackage.error) {
    return `MRXS package · ${fileCountLabel} · Completeness unknown: ${mrxsPackage.error}`;
  }

  const listedMissingFiles = mrxsPackage.missingFiles.slice(0, MAX_LISTED_MISSING_MRXS_FILES);
  const unlistedMissingFileCount = mrxsPackage.missingFiles.length - listedMissingFiles.length;
  const missingFilesLabel = join(
    compact([join(listedMissingFiles, ', '), unlistedMissingFileCount > 0 ? `+${unlistedMissingFileCount} more` : '']),
    ', '
  );

  return `MRXS package · ${fileCountLabel} · Incomplete, missing ${missingFilesLabel}`;
};

const renderMrxsPackageCaption = (mrxsPackage?: MrxsUploadPackageSummary | null) => {
  if (!mrxsPackage) {
    return null;
  }

  return (
    <Typography
      variant="caption"
      color={mrxsPackage.isComplete ? 'text.secondary' : 'text.primary'}
      sx={mrxsPackage.isComplete ? undefined : { fontWeight: 600 }}
      title={mrxsPackage.missingFiles.length ? join(mrxsPackage.missingFiles, '\n') : undefined}
    >
      {getMrxsPackageCaption(mrxsPackage)}
    </Typography>
  );
};

const uploadsGridCellContainerSx = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'flex-start',
};

const renderCopyableIdentifier = ({
  value,
  label,
  rowLabel,
  query,
  normalize = false,
  variant = 'body2',
}: {
  value?: string | null;
  label: string;
  rowLabel: string;
  query?: string;
  normalize?: boolean;
  variant?: 'body2' | 'caption';
}) => {
  if (!value) {
    return '—';
  }

  return (
    <CopyableText
      variant={variant}
      textForCopy={value}
      tooltipText={`Copy ${label}`}
      copyWithButton="right"
      copyButtonProps={{
        size: 'small',
        edge: 'end',
        'aria-label': `Copy ${label} for ${rowLabel}`,
      }}
      copyIconProps={{ fontSize: 'small' }}
      parentGridProps={{ sx: { maxWidth: '100%' } }}
      whiteSpace="normal"
      overflow="visible"
      textOverflow="clip"
      sx={{ cursor: 'default', fontFamily: 'monospace', wordBreak: 'break-all', color: 'text.secondary' }}
    >
      <HighlightedText value={value} query={query} normalize={normalize} />
    </CopyableText>
  );
};

const UploadsTable = ({
  rows,
  selectedStudyId,
  onDownloadManifest,
  onExecuteUploadIngest,
  onRunManualPostIngestPipeline,
  onExecuteBulkUploadIngest,
  onAssignUploadToOwnCase,
  onAssignSelectedUploadsToSingleCase,
  onAssignSelectedUploadsToOwnCases,
  onSetUploadVisibility,
  onRemoveCaseAssignments,
  pendingAssignmentRemovalIds = [],
  canExecuteUploadIngest = false,
  onHideUploads,
  onRestoreUploads,
  canReingestUploadedSlides = false,
  canRunManualPostIngestPipeline = false,
  canManageUploadVisibility = false,
  downloadingManifestKey,
  uploadVisibilityPendingOperations = {},
  lifecycleRequestState = 'ready',
}: UploadsTableProps) => {
  const { labId } = useCurrentLabId();
  const { getLinkToCasePage } = useNavigationToViewerPage();
  // The uploads list refreshes every 2 minutes; poll only the slides whose tiles are still generating.
  const { data: liveTileGenerationBySlideId } = useSlidesTileGenerationStatus({
    labId,
    studyId: selectedStudyId,
    slideIds: compact(map(rows, (row) => (isTileGenerationActive(row.tileGenerationJob?.status) ? row.slideId : null))),
  });
  const showCaseColumn = some(rows, (row) => Boolean(row.cases?.length));
  const showSlidesColumn = some(rows, (row) => Boolean(row.slideId));
  const showHiddenProvenanceColumns = some(rows, (row) => row.isHidden);
  const showActionColumn = some(rows, (row) => {
    const lifecycle = getUploadDashboardLifecycle(row, lifecycleRequestState);
    const canShowManifestAction = Boolean(row.isManifest && onDownloadManifest);
    const canShowUploadIngestAction = Boolean(
      onExecuteUploadIngest &&
        canExecuteUploadIngest &&
        lifecycleRequestState === 'ready' &&
        isUploadIngestActionAvailable(row, lifecycle, canReingestUploadedSlides)
    );
    const canShowManualPostIngestAction = Boolean(
      row.slideId &&
        row.canManualRunPostIngestPipeline &&
        onRunManualPostIngestPipeline &&
        canRunManualPostIngestPipeline
    );
    const canShowAssignMissingCaseAction = Boolean(
      isAssignableMissingCaseRow(row, lifecycle) && onAssignUploadToOwnCase
    );
    const canShowVisibilityAction = Boolean(
      canManageUploadVisibility && onSetUploadVisibility && (row.isHidden || row.canHide)
    );

    return (
      canShowManifestAction ||
      canShowUploadIngestAction ||
      canShowManualPostIngestAction ||
      canShowAssignMissingCaseAction ||
      Boolean(row.rejectedIngestCaseAssignments && onRemoveCaseAssignments) ||
      canShowVisibilityAction
    );
  });
  const apiRef = useGridApiRef();
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>(DEFAULT_READ_ONLY_GRID_FILTER_MODEL);
  const [filteredRowIds, setFilteredRowIds] = React.useState<GridRowId[] | null>(null);
  const syncFilteredRowIds = React.useCallback(() => {
    if (!apiRef.current?.state) {
      return;
    }

    const nextIds = gridFilteredSortedRowIdsSelector(apiRef);
    setFilteredRowIds((current) => {
      if (current && current.length === nextIds.length && current.every((id, index) => id === nextIds[index])) {
        return current;
      }

      return nextIds;
    });
  }, [apiRef]);
  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [density, setDensity] = React.useState<GridDensity>('compact');
  const showBulkUploadIngestAction = Boolean(
    lifecycleRequestState === 'ready' &&
      onExecuteBulkUploadIngest &&
      canExecuteUploadIngest &&
      some(rows, (row) =>
        isUploadIngestActionAvailable(
          row,
          getUploadDashboardLifecycle(row, lifecycleRequestState),
          canReingestUploadedSlides
        )
      )
  );
  const showBulkCaseAssignmentActions = Boolean(
    onAssignSelectedUploadsToSingleCase &&
      onAssignSelectedUploadsToOwnCases &&
      some(rows, (row) => isAssignableMissingCaseRow(row, getUploadDashboardLifecycle(row, lifecycleRequestState)))
  );
  const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowId[]>([]);
  const canSelectUploads = Boolean(
    (canManageUploadVisibility && (onHideUploads || onRestoreUploads)) ||
      (canExecuteUploadIngest && onExecuteBulkUploadIngest) ||
      showBulkCaseAssignmentActions
  );
  const selectedRows = rows.filter((row) => rowSelectionModel.includes(row.id));
  const selectionIsReady = lifecycleRequestState === 'ready';
  const selectedHideableRows = selectedRows.filter(
    (row) => selectionIsReady && row.canHide && !row.isHidden && !uploadVisibilityPendingOperations[row.objectKey]
  );
  const selectedRestorableRows = selectedRows.filter(
    (row) => selectionIsReady && row.isHidden && !uploadVisibilityPendingOperations[row.objectKey]
  );
  const selectedIngestRows = selectedRows.filter(
    (row) =>
      !uploadVisibilityPendingOperations[row.objectKey] &&
      isUploadIngestActionAvailable(
        row,
        getUploadDashboardLifecycle(row, lifecycleRequestState),
        canReingestUploadedSlides
      )
  );
  const selectedIngestGroups = ['Ingest', 'Retry ingest', 'Re-ingest'].map((label) => ({
    label,
    rows: selectedIngestRows.filter(
      (row) => getUploadIngestActionLabel(row, getUploadDashboardLifecycle(row, lifecycleRequestState)) === label
    ),
  }));
  React.useEffect(() => {
    setRowSelectionModel((current) => {
      const next = current.filter((rowId) => rows.some((row) => row.id === rowId));
      return next.length === current.length ? current : next;
    });
  }, [rows]);
  const [bulkCaseActionsAnchorEl, setBulkCaseActionsAnchorEl] = React.useState<null | HTMLElement>(null);
  const quickFilterQuery = React.useMemo(() => getQuickFilterQuery(filterModel), [filterModel]);
  const hasActiveGridFilters = React.useMemo(() => hasActiveReadOnlyGridFilters(filterModel), [filterModel]);
  const filteredRowIdSet = React.useMemo(
    () => (filteredRowIds ? new Set(filteredRowIds.map(String)) : null),
    [filteredRowIds]
  );
  const bulkHideableOtherFiles = React.useMemo(
    () =>
      getBulkHideableOtherFiles(rows, filteredRowIdSet).filter(
        (row) => !uploadVisibilityPendingOperations[row.objectKey]
      ),
    [filteredRowIdSet, rows, uploadVisibilityPendingOperations]
  );
  React.useEffect(() => {
    syncFilteredRowIds();
  }, [filterModel, rows, syncFilteredRowIds]);

  const showBulkHideOtherFiles = Boolean(
    canManageUploadVisibility && onHideUploads && bulkHideableOtherFiles.length > 1
  );
  const resolveBulkHideableOtherFiles = React.useCallback(() => {
    const visibleRowIds = apiRef.current?.state
      ? new Set(gridFilteredSortedRowIdsSelector(apiRef).map(String))
      : filteredRowIdSet;

    return getBulkHideableOtherFiles(rows, visibleRowIds).filter(
      (row) => !uploadVisibilityPendingOperations[row.objectKey]
    );
  }, [apiRef, filteredRowIdSet, rows, uploadVisibilityPendingOperations]);
  const selectedAssignableRows = React.useMemo(
    () =>
      filter(
        rows,
        (row) =>
          includes(rowSelectionModel, row.id) &&
          !uploadVisibilityPendingOperations[row.objectKey] &&
          isAssignableMissingCaseRow(row, getUploadDashboardLifecycle(row, lifecycleRequestState))
      ),
    [lifecycleRequestState, rows, rowSelectionModel, uploadVisibilityPendingOperations]
  );

  const handleFilterModelChange = React.useCallback((nextFilterModel: GridFilterModel) => {
    setFilterModel(nextFilterModel);
    setPaginationModel((currentPaginationModel) =>
      currentPaginationModel.page === 0 ? currentPaginationModel : { ...currentPaginationModel, page: 0 }
    );
  }, []);

  const columns = React.useMemo<GridColDef<UploadDashboardRow>[]>(() => {
    const resolvedColumns: GridColDef<UploadDashboardRow>[] = [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1.2,
        minWidth: 260,
        valueGetter: (_value, row) => `${row.name} ${getDisplayedPath(row.path)}`.trim(),
        renderCell: ({ row }) => {
          const rowLabel = row.fileName || row.name;
          const displayedPath = getDisplayedPath(row.path);
          const fullUploadPath = getFullUploadPath(row);

          return (
            <UploadFileCell
              copyAriaLabel={`Copy S3 path for ${rowLabel}`}
              copyTooltip="Copy S3 path"
              details={renderMrxsPackageCaption(row.mrxsPackage)}
              displayName={row.name}
              displayedPath={displayedPath}
              fullPath={fullUploadPath}
              query={quickFilterQuery}
            />
          );
        },
      },
      {
        field: 'type',
        headerName: 'Type',
        minWidth: 110,
        valueGetter: (_value, row) => (isExtensionlessUploadRow(row) ? 'No extension' : row.type || '—'),
        renderCell: ({ row }) => {
          const isExtensionlessRow = isExtensionlessUploadRow(row);

          return (
            <Box sx={uploadsGridCellContainerSx}>
              <Stack spacing={0.25} sx={{ minWidth: 0, width: '100%' }}>
                <Typography
                  variant="body2"
                  color={isExtensionlessRow ? 'warning.main' : 'text.primary'}
                  sx={isExtensionlessRow ? { fontWeight: 600 } : undefined}
                >
                  <HighlightedText
                    value={isExtensionlessRow ? 'No extension' : row.type || '—'}
                    query={quickFilterQuery}
                  />
                </Typography>
              </Stack>
            </Box>
          );
        },
      },
      {
        field: 'size',
        headerName: 'Size',
        type: 'number',
        minWidth: 110,
        valueGetter: (_value, row) => row.size,
        getApplyQuickFilterFn: getUploadSizeQuickFilterFn,
        renderCell: ({ row }) => (
          <Box sx={uploadsGridCellContainerSx}>
            <HighlightedText value={getDisplaySize(row.size)} query={quickFilterQuery} />
          </Box>
        ),
      },
      {
        field: 'date',
        headerName: 'Upload date',
        type: 'dateTime',
        minWidth: 190,
        filterOperators: getReadOnlyDateTimeFilterOperators(),
        valueGetter: (_value, row) => getUploadDateValue(row.date),
        getApplyQuickFilterFn: getUploadDateQuickFilterFn,
        renderCell: ({ row }) => (
          <Box sx={uploadsGridCellContainerSx}>
            <HighlightedText value={getDisplayDate(row.date)} query={quickFilterQuery} />
          </Box>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.7,
        minWidth: 180,
        valueGetter: (_value, row) =>
          compact([
            row.latestIngestJob?.startedAt,
            row.latestIngestJob?.status,
            row.latestIngestJob?.statusMessage,
            row.latestIngestJob?.id,
          ]).join(' '),
        renderCell: ({ row }) => {
          const rowLabel = row.fileName || row.name;
          const lifecycle = getUploadDashboardLifecycle(row, lifecycleRequestState);
          const jobReference = getUploadStatusJobReference(row, lifecycle);
          const tileGeneration =
            (row.slideId && liveTileGenerationBySlideId?.[row.slideId]) || row.tileGenerationJob || null;

          return (
            <Box sx={{ ...uploadsGridCellContainerSx, alignItems: 'flex-start', py: 1 }}>
              <Stack spacing={0.5} sx={{ minWidth: 0, width: '100%' }}>
                <IngestJobStatusCell
                  job={jobReference?.job || null}
                  rowLabel={rowLabel}
                  isArchived={Boolean(jobReference?.archived)}
                  jobUrl={
                    selectedStudyId && jobReference
                      ? getUrlToJobDetails({
                          studyId: selectedStudyId,
                          labId,
                          jobId: jobReference.job.id,
                          archived: jobReference.archived,
                        })
                      : null
                  }
                />
                {tileGeneration ? (
                  <TileGenerationStatusLine
                    status={tileGeneration}
                    jobUrl={
                      selectedStudyId
                        ? getUrlToJobDetails({ studyId: selectedStudyId, labId, jobId: tileGeneration.jobId })
                        : null
                    }
                  />
                ) : null}
              </Stack>
            </Box>
          );
        },
      },
      {
        field: 'ingestTimeline',
        headerName: 'Timeline',
        flex: 1.2,
        minWidth: 320,
        sortable: false,
        valueGetter: (_value, row) => getUploadTimelineSearchText(getUploadTimelineIngests(row)),
        renderCell: ({ row }) => {
          const rowLabel = row.fileName || row.name;
          const timelineIngests = getUploadTimelineIngests(row);

          if (!timelineIngests.length) {
            return (
              <Box sx={uploadsGridCellContainerSx}>
                <Typography variant="body2">—</Typography>
              </Box>
            );
          }

          return (
            <Box sx={{ ...uploadsGridCellContainerSx, alignItems: 'flex-start', py: 1 }}>
              <UploadIngestTimeline
                ingests={timelineIngests}
                rowLabel={rowLabel}
                showArchiveAction={Boolean(row.duplicateIngests?.length)}
                getJobUrl={(jobId, archived) =>
                  selectedStudyId
                    ? getUrlToJobDetails({
                        studyId: selectedStudyId,
                        labId,
                        jobId,
                        archived: archived ? true : undefined,
                      })
                    : null
                }
              />
            </Box>
          );
        },
      },
    ];

    if (showHiddenProvenanceColumns) {
      resolvedColumns.splice(
        4,
        0,
        {
          field: 'hiddenAt',
          headerName: 'Hidden at',
          type: 'dateTime',
          minWidth: 190,
          filterOperators: getReadOnlyDateTimeFilterOperators(),
          valueGetter: (_value, row) => getUploadDateValue(row.hiddenAt),
          getApplyQuickFilterFn: (value) => {
            const normalizedQuery = String(value ?? '')
              .trim()
              .toLowerCase();

            if (!normalizedQuery) {
              return null;
            }

            return (_columnValue, row) => {
              const displayValue = row.hiddenAt ? getDisplayDate(row.hiddenAt) : 'Unavailable';
              return displayValue.toLowerCase().includes(normalizedQuery);
            };
          },
          renderCell: ({ row }) => (
            <Box sx={uploadsGridCellContainerSx}>
              <HighlightedText
                value={row.hiddenAt ? getDisplayDate(row.hiddenAt) : 'Unavailable'}
                query={quickFilterQuery}
              />
            </Box>
          ),
        },
        {
          field: 'hiddenBy',
          headerName: 'Hidden by',
          minWidth: 260,
          valueGetter: (_value, row) => row.hiddenBy || 'Unavailable',
          renderCell: ({ row }) => (
            <Box sx={uploadsGridCellContainerSx}>
              {row.hiddenBy ? (
                <CopyableText
                  variant="body2"
                  textForCopy={row.hiddenBy}
                  tooltipText="Copy user ID"
                  copyWithButton="right"
                  copyButtonProps={{
                    size: 'small',
                    edge: 'end',
                    'aria-label': `Copy hidden-by user ID for ${row.fileName || row.name}`,
                  }}
                  copyIconProps={{ fontSize: 'small' }}
                  parentGridProps={{ sx: { maxWidth: '100%' } }}
                >
                  <HighlightedText value={row.hiddenBy} query={quickFilterQuery} />
                </CopyableText>
              ) : (
                <HighlightedText value="Unavailable" query={quickFilterQuery} />
              )}
            </Box>
          ),
        }
      );
    }

    if (showCaseColumn) {
      resolvedColumns.push({
        field: 'cases',
        headerName: 'Case',
        flex: 1,
        minWidth: 220,
        valueGetter: (_value, row) => getUploadCaseValue(row.cases),
        renderCell: ({ row }) => {
          if (!row.cases?.length) {
            return <Box sx={uploadsGridCellContainerSx}>—</Box>;
          }

          return (
            <Box sx={uploadsGridCellContainerSx}>
              <Stack spacing={0.25} sx={{ minWidth: 0, width: '100%' }}>
                {map(row.cases, (uploadCase) => (
                  <Link
                    key={uploadCase.id}
                    component={RouterLink}
                    to={getLinkToCasePage({
                      caseId: uploadCase.id,
                      caseStudyId: uploadCase.studyId,
                      labId,
                    })}
                    color="secondary"
                    sx={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}
                  >
                    <HighlightedText value={uploadCase.displayName} query={quickFilterQuery} />
                  </Link>
                ))}
              </Stack>
            </Box>
          );
        },
      });
    }

    if (showSlidesColumn) {
      resolvedColumns.push({
        field: 'slideId',
        headerName: 'Slide',
        flex: 1,
        minWidth: 250,
        valueGetter: (_value, row) => row.slideId || '',
        renderCell: ({ row }) => {
          if (!row.slideId) {
            return <Box sx={uploadsGridCellContainerSx}>—</Box>;
          }

          const rowLabel = row.fileName || row.name;
          const slidePreviewSlides: Slide[] = [{ id: row.slideId, labId, originalFileName: row.fileName }];

          return (
            <Box sx={uploadsGridCellContainerSx}>
              <SlidePreviewCell
                slides={slidePreviewSlides}
                labId={labId}
                caseStudyId={selectedStudyId}
                summaryContent={renderCopyableIdentifier({
                  value: row.slideId,
                  label: 'slide ID',
                  rowLabel,
                  query: quickFilterQuery,
                  normalize: true,
                  variant: 'caption',
                })}
              />
            </Box>
          );
        },
      });
    }

    if (showActionColumn) {
      resolvedColumns.push({
        field: 'manifestAction',
        headerName: 'Action',
        minWidth: 168,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const lifecycle = getUploadDashboardLifecycle(row, lifecycleRequestState);
          const canShowUploadIngestAction = Boolean(
            onExecuteUploadIngest &&
              canExecuteUploadIngest &&
              lifecycleRequestState === 'ready' &&
              isUploadIngestActionAvailable(row, lifecycle, canReingestUploadedSlides)
          );
          const canShowManualPostIngestAction = Boolean(
            row.slideId &&
              row.canManualRunPostIngestPipeline &&
              onRunManualPostIngestPipeline &&
              canRunManualPostIngestPipeline
          );
          const canShowAssignMissingCaseAction = Boolean(
            isAssignableMissingCaseRow(row, lifecycle) && onAssignUploadToOwnCase
          );
          const canShowVisibilityAction = Boolean(
            canManageUploadVisibility && onSetUploadVisibility && (row.isHidden || row.canHide)
          );
          const isDownloadingManifest = downloadingManifestKey === row.objectKey;
          const pendingVisibilityOperation = uploadVisibilityPendingOperations[row.objectKey];
          const isUpdatingVisibility = Boolean(pendingVisibilityOperation);
          const visibilityOperation = pendingVisibilityOperation || (row.isHidden ? 'restore' : 'hide');
          const isRestoringVisibility = visibilityOperation === 'restore';

          return (
            <Box sx={uploadsGridCellContainerSx}>
              <Stack spacing={0.5} alignItems="flex-start">
                {canShowAssignMissingCaseAction ? (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => onAssignUploadToOwnCase?.(row)}
                    aria-label={`Assign ${row.fileName || row.name} to a case`}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    Assign to case
                  </Button>
                ) : null}
                {canShowUploadIngestAction ? (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => onExecuteUploadIngest?.(row)}
                    aria-label={`${getUploadIngestActionLabel(row, lifecycle)} ${row.fileName || row.name}`}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    {getUploadIngestActionLabel(row, lifecycle)}
                  </Button>
                ) : null}
                {canShowManualPostIngestAction ? (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => onRunManualPostIngestPipeline?.(row)}
                    aria-label={`Run initial image analysis for ${row.fileName || row.name}`}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    Run initial image analysis
                  </Button>
                ) : null}
                {row.isManifest && onDownloadManifest ? (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => onDownloadManifest(row)}
                    disabled={isDownloadingManifest}
                    endIcon={isDownloadingManifest ? <CircularProgress size={16} color="inherit" /> : undefined}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    {isDownloadingManifest ? 'Downloading...' : 'Download'}
                  </Button>
                ) : null}
                {canShowVisibilityAction ? (
                  <Button
                    size="small"
                    variant={isRestoringVisibility ? 'contained' : 'text'}
                    color={isRestoringVisibility ? 'secondary' : 'inherit'}
                    startIcon={
                      isUpdatingVisibility ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : isRestoringVisibility ? (
                        <RestoreOutlinedIcon />
                      ) : undefined
                    }
                    disabled={isUpdatingVisibility}
                    onClick={() => onSetUploadVisibility?.(row, visibilityOperation)}
                    aria-label={`${isRestoringVisibility ? 'Restore' : 'Hide'} ${row.fileName || row.name}`}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    {isUpdatingVisibility
                      ? isRestoringVisibility
                        ? 'Restoring...'
                        : 'Hiding...'
                      : isRestoringVisibility
                      ? 'Restore'
                      : 'Hide'}
                  </Button>
                ) : null}
                {row.rejectedIngestCaseAssignments && onRemoveCaseAssignments ? (
                  <Tooltip
                    title={
                      row.rejectedIngestCaseAssignments.canRemove
                        ? ''
                        : 'Requires case-assignment and job-archive permission, with access to every affected study.'
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        color="inherit"
                        startIcon={
                          pendingAssignmentRemovalIds.includes(row.rejectedIngestCaseAssignments.jobId) ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <LinkOffOutlinedIcon />
                          )
                        }
                        disabled={
                          !row.rejectedIngestCaseAssignments.canRemove ||
                          lifecycleRequestState !== 'ready' ||
                          pendingAssignmentRemovalIds.includes(row.rejectedIngestCaseAssignments.jobId)
                        }
                        onClick={() => onRemoveCaseAssignments(row)}
                        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                      >
                        Remove case assignments
                      </Button>
                    </span>
                  </Tooltip>
                ) : null}
              </Stack>
            </Box>
          );
        },
      });
    }

    return resolvedColumns;
  }, [
    downloadingManifestKey,
    uploadVisibilityPendingOperations,
    canExecuteUploadIngest,
    canManageUploadVisibility,
    canRunManualPostIngestPipeline,
    canReingestUploadedSlides,
    getLinkToCasePage,
    labId,
    lifecycleRequestState,
    liveTileGenerationBySlideId,
    onDownloadManifest,
    onAssignUploadToOwnCase,
    onExecuteUploadIngest,
    onRunManualPostIngestPipeline,
    onSetUploadVisibility,
    onRemoveCaseAssignments,
    pendingAssignmentRemovalIds,
    quickFilterQuery,
    selectedStudyId,
    showActionColumn,
    showCaseColumn,
    showHiddenProvenanceColumns,
    showSlidesColumn,
  ]);

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 0 }}>
      <Paper sx={{ width: '100%', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <ReadOnlyDataGrid
          apiRef={apiRef}
          aria-label="Uploaded files grid"
          rows={rows}
          columns={columns}
          onStateChange={syncFilteredRowIds}
          getRowId={(row) => row.id}
          getRowHeight={autoGridRowHeight}
          pagination
          paginationMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          initialState={{
            sorting: {
              sortModel: [{ field: 'date', sort: 'desc' }],
            },
            pagination: {
              paginationModel: {
                page: 0,
                pageSize: 25,
              },
            },
          }}
          height="100%"
          density={density}
          onDensityChange={setDensity}
          disableRowSelectionOnClick
          checkboxSelection={canSelectUploads}
          rowSelectionModel={rowSelectionModel}
          keepNonExistentRowsSelected
          onRowSelectionModelChange={(nextRowSelectionModel) =>
            setRowSelectionModel(nextRowSelectionModel as GridRowId[])
          }
          isRowSelectable={() => canSelectUploads}
          disableVirtualization={DISABLE_VIRTUALIZATION_FOR_TESTS}
          hideFooterSelectedRowCount
          hasActiveFilters={hasActiveGridFilters}
          activeFilterColor="primary"
          slotProps={{
            toolbar: {
              toolbarActions: (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {selectedRows.length > 0 && (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      aria-label="Selected upload actions"
                    >
                      <Typography variant="body2" role="status" aria-label="Selected uploads">
                        {selectedRows.length} selected
                      </Typography>
                      {!selectionIsReady && (
                        <Typography variant="caption">Refresh uploads before taking actions.</Typography>
                      )}
                      {canManageUploadVisibility && onHideUploads && (
                        <Tooltip
                          title={`${selectedHideableRows.length} of ${selectedRows.length} selected files can be hidden`}
                        >
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              color="warning"
                              startIcon={<VisibilityOffOutlinedIcon />}
                              disabled={!selectedHideableRows.length}
                              onClick={() => onHideUploads(selectedHideableRows)}
                            >
                              Hide ({selectedHideableRows.length})
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {canManageUploadVisibility && onRestoreUploads && selectedRows.some((row) => row.isHidden) && (
                        <Tooltip
                          title={`${selectedRestorableRows.length} of ${selectedRows.length} selected files can be restored`}
                        >
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              color="secondary"
                              startIcon={<UnarchiveOutlinedIcon />}
                              disabled={!selectedRestorableRows.length}
                              onClick={() => onRestoreUploads(selectedRestorableRows)}
                            >
                              Restore ({selectedRestorableRows.length})
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {canExecuteUploadIngest && onExecuteBulkUploadIngest && !selectedIngestRows.length && (
                        <Tooltip title="No selected files are eligible for ingest in their current state.">
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              color="secondary"
                              startIcon={<PlayArrowOutlinedIcon />}
                              disabled
                            >
                              Ingest (0)
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {canExecuteUploadIngest &&
                        onExecuteBulkUploadIngest &&
                        selectedIngestGroups
                          .filter((group) => group.rows.length > 0)
                          .map((group) => (
                            <Tooltip
                              describeChild
                              key={group.label}
                              title={`${group.rows.length} of ${selectedRows.length} selected files can ${
                                group.label === 'Ingest' ? 'be ingested' : 'run this ingest action'
                              }`}
                            >
                              <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                startIcon={
                                  group.label === 'Ingest' ? <PlayArrowOutlinedIcon /> : <ReplayOutlinedIcon />
                                }
                                onClick={() => onExecuteBulkUploadIngest(group.rows)}
                              >
                                {group.label} ({group.rows.length})
                              </Button>
                            </Tooltip>
                          ))}
                    </Stack>
                  )}
                  {showBulkCaseAssignmentActions ? (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={(event) => setBulkCaseActionsAnchorEl(event.currentTarget)}
                        disabled={selectedAssignableRows.length === 0}
                        endIcon={<ArrowDropDownIcon />}
                      >
                        Assign slides to cases{selectedRows.length ? ` (${selectedAssignableRows.length})` : ''}
                      </Button>
                      <Menu
                        anchorEl={bulkCaseActionsAnchorEl}
                        open={Boolean(bulkCaseActionsAnchorEl)}
                        onClose={() => setBulkCaseActionsAnchorEl(null)}
                      >
                        <MenuItem
                          onClick={() => {
                            onAssignSelectedUploadsToSingleCase?.(selectedAssignableRows);
                            setBulkCaseActionsAnchorEl(null);
                          }}
                        >
                          Group into one case
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            onAssignSelectedUploadsToOwnCases?.(selectedAssignableRows);
                            setBulkCaseActionsAnchorEl(null);
                          }}
                        >
                          One case per slide
                        </MenuItem>
                      </Menu>
                    </>
                  ) : null}
                  {showBulkUploadIngestAction ? (
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      onClick={() => onExecuteBulkUploadIngest?.()}
                    >
                      Bulk ingest folder
                    </Button>
                  ) : null}
                  {showBulkHideOtherFiles ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={() => onHideUploads?.(resolveBulkHideableOtherFiles())}
                      sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                    >
                      {`Hide ${bulkHideableOtherFiles.length} other files`}
                    </Button>
                  ) : null}
                </Stack>
              ),
            },
          }}
          noResultsTitle="No uploaded files match the current grid filters"
          noResultsSubtitle="Try changing the quick filter or column filters to see rows again."
          sx={{
            '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
              py: 0.75,
            },
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
              py: 1.25,
            },
            '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
              py: 1.75,
            },
            '&& .MuiDataGrid-cell': {
              alignItems: 'flex-start',
            },
            '&& .MuiDataGrid-row--dynamicHeight > .MuiDataGrid-cell': {
              alignItems: 'flex-start',
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default UploadsTable;
